/**
 * custom-roast-ui.js
 * CCC custom feature — drives the product-page roast status card
 * and the cart page roast banner.
 *
 * Listens to Horizon's native events:
 *   - 'variant:update'  → update product-page status card
 *   - 'cart:update'     → update cart-page roast banner
 *
 * DOM markers expected on the page
 * ─────────────────────────────────
 * Product page (block/custom-roast-status.liquid):
 *   [data-cru-upcoming]        — upcoming-roast status card (hidden initially)
 *   [data-cru-in-stock]        — in-stock status card (hidden initially)
 *   [data-cru-roast-date]      — text node that receives the formatted roast date
 *   [data-cru-variants]        — <script type="application/json"> containing variant data
 *   [data-ccc-upcoming-roast]  — Upcoming Roast date chip / select option (filled here)
 *
 * Cart page (section/main-cart.liquid injects snippets/custom-roast-cart-banner.liquid):
 *   [data-cru-cart-banner]     — banner wrapper (hidden initially)
 *   [data-cru-cart-roast-date] — text node inside banner for roast date
 *   [data-cru-cart-ship-range] — text node inside banner for shipping range
 */

import { ThemeEvents } from '@theme/events';
import { cachedRoastDate, cachedRoastDateSync, formatShortRoastDate } from '@theme/custom-roast-manager';
import { getUnifiedCartMessage } from '@theme/custom-dispatch-manager';

// ─── Product Page ────────────────────────────────────────────────────────────

let _variantMap = null; // Map<number, { id, title }>

// Stale-result guard: tracks the most recently requested variant ID.
// If another variant:update fires while an async cachedRoastDate() call is
// in-flight, the earlier call discards its result when it resolves.
let _pendingVariantId = null;

// Debounce timer — Horizon fires variant:update once per option group on page
// load (e.g. 2 options = 2 events). Debouncing collapses the burst into one.
let _debounceTimer = null;
const DEBOUNCE_MS = 40;

function loadVariantMap() {
  if (_variantMap) return;
  const el = document.querySelector('[data-cru-variants]');
  if (!el) return;
  try {
    const arr = JSON.parse(el.textContent);
    _variantMap = new Map(arr.map((v) => [v.id, v]));
  } catch (e) {
    console.warn('[CCC] custom-roast-ui: failed to parse variant data', e);
  }
}

async function applyProductPageStatus(variantId) {
  const roastInfoPromise = stampUpcomingRoastControls();

  loadVariantMap();
  if (!_variantMap) {
    await roastInfoPromise;
    return;
  }

  const variant = _variantMap.get(variantId);
  if (!variant) {
    await roastInfoPromise;
    return;
  }

  const upcomingEl = document.querySelector('[data-cru-upcoming]');
  const inStockEl = document.querySelector('[data-cru-in-stock]');
  const isUpcoming = variant.title && variant.title.toLowerCase().includes('upcoming roast');

  // Apply visibility synchronously first — this is the definitive state.
  // Do not await before writing hidden flags; stale async results below are
  // guarded by _pendingVariantId so they cannot overwrite this decision.
  if (upcomingEl && inStockEl) {
    // A sold-out Upcoming Roast gets no "Roasting on" note: the stock card
    // already says it's out and when it's due back.
    if (isUpcoming && variant.available) {
      inStockEl.hidden = true;
      upcomingEl.hidden = false;
    } else if (variant.available) {
      upcomingEl.hidden = true;
      inStockEl.hidden = false;
    } else {
      upcomingEl.hidden = true;
      inStockEl.hidden = true;
    }
  }

  if (!isUpcoming || !variant.available || !upcomingEl) {
    await roastInfoPromise;
    return;
  }

  // Fill in the roast date — async, but guarded against stale results.
  _pendingVariantId = variantId;
  const info = await roastInfoPromise;

  // Discard if a newer variant:update has already taken over.
  if (_pendingVariantId !== variantId) return;

  writeRoastCopy(upcomingEl, info);
}

/**
 * Stamp the calendar date onto Upcoming Roast pills and selects.
 * Radio / option values stay "Upcoming Roast" so title matching is unchanged.
 * @param {object|null} info
 */
function writeUpcomingControlDate(info) {
  const short = info?.roastDate ? formatShortRoastDate(info.roastDate) : '';

  for (const el of document.querySelectorAll('[data-ccc-upcoming-roast]')) {
    if (el instanceof HTMLOptionElement) {
      const base = el.getAttribute('data-ccc-upcoming-base') || el.value || 'Upcoming Roast';
      const unavailable = el.getAttribute('data-ccc-upcoming-unavailable') || '';
      let text = short ? `${base}, ${short}` : base;
      if (unavailable) text += ` - ${unavailable}`;
      if (el.textContent !== text) el.textContent = text;
      continue;
    }

    if (el.textContent !== short) el.textContent = short;

    const input = el.closest('label')?.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) continue;

    const currentAria = input.getAttribute('aria-label') || '';
    let soldSuffix = '';
    if (input.getAttribute('data-option-available') === 'false') {
      const dash = currentAria.lastIndexOf(' - ');
      soldSuffix = dash >= 0 ? currentAria.slice(dash) : '';
    }
    const aria = short
      ? `${input.value}, ${short}${soldSuffix}`
      : `${input.value}${soldSuffix}`;
    if (input.getAttribute('aria-label') !== aria) {
      input.setAttribute('aria-label', aria);
    }
  }
}

/**
 * Paint Upcoming Roast dates from localStorage immediately, then reconcile
 * with the calendar fetch. Safe to call on leftover-date variants too.
 * @returns {Promise<object|null>}
 */
async function stampUpcomingRoastControls() {
  const cached = cachedRoastDateSync();
  if (cached) {
    writeUpcomingControlDate(cached);
  }
  const info = await cachedRoastDate();
  writeUpcomingControlDate(info);
  return info;
}

/**
 * Write the roast status card copy for the resolved roast info.
 * Single source of truth — used by both the variant-change path and the
 * initial-load path so the wording can never drift between the two.
 * @param {HTMLElement} upcomingEl
 * @param {object|null} info — cachedRoastDate() result (null = calendar unavailable)
 */
function writeRoastCopy(upcomingEl, info) {
  let dateEl = upcomingEl.querySelector('[data-cru-roast-date]');
  const subEl = upcomingEl.querySelector('[data-cru-roast-sub]');
  if (!dateEl) return;
  // Old Liquid markup has <div>Roasting on <span data-cru-roast-date>…</span></div>
  // Target the parent div so the 'Roasting on ' prefix gets replaced too.
  if (dateEl.tagName === 'SPAN') dateEl = dateEl.parentElement;

  const shipTogether = 'Your entire order will ship together once your coffee is roasted.';
  let dateText;
  let subText = shipTogether;

  if (!info) {
    dateText = 'Roasting on the next roast day';
  } else if (info.isToday && info.beforeCutoff) {
    dateText = 'Roasting today, order by 7:00am';
  } else if (info.roastDayPastCutoff && !info.afterLeftoverTime) {
    dateText = `You have missed today's roasting cutoff (7:00am) so if you order now it will be roasted on ${info.formatted}`;
    subText = "Check in after 6:00pm today for any leftover coffee from today's roast if you need it shipped asap.";
  } else if (info.isTomorrow) {
    dateText = `Roasting tomorrow, ${info.formatted}`;
  } else {
    dateText = `Roasting on ${info.formatted}`;
  }

  dateEl.textContent = dateText;
  if (subEl) subEl.textContent = subText;
}

// ─── Cart Page ───────────────────────────────────────────────────────────────

/**
 * Live cart delay state from the morphing cart-products island.
 * Prefer this over the banner's initial Liquid attrs — those can stay stale
 * (e.g. banner stayed hidden after qty was raised into partial backorder).
 * @returns {{ hasCoffee: boolean, hasPreorders: boolean, preorderItems: Array<object> } | null}
 */
function readLiveCartDelayState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    const state = JSON.parse(el.textContent || '{}');
    return {
      hasCoffee: Boolean(state.hasCoffee),
      hasPreorders: Boolean(state.hasPreorders),
      preorderItems: Array.isArray(state.preorderItems) ? state.preorderItems : [],
    };
  } catch (e) {
    console.warn('[CCC] Failed to parse cart delay state', e);
    return null;
  }
}

async function applyCartBanner() {
  const bannerEl = document.querySelector('[data-cru-cart-banner]');
  if (!bannerEl) return;

  const live = readLiveCartDelayState();

  let hasCoffee = bannerEl.getAttribute('data-has-coffee') === 'true';
  let hasPreorders = bannerEl.getAttribute('data-has-preorders') === 'true';

  /** @type {Array<{type?: string, vendor?: string, estimatedRestockDate?: string|null, kind?: string}>} */
  let preorderItems = [];

  if (live) {
    hasCoffee = live.hasCoffee;
    hasPreorders = live.hasPreorders;
    preorderItems = live.preorderItems;
    bannerEl.setAttribute('data-has-coffee', hasCoffee ? 'true' : 'false');
    bannerEl.setAttribute('data-has-preorders', hasPreorders ? 'true' : 'false');
    bannerEl.toggleAttribute('hidden', !(hasCoffee || hasPreorders));

    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      preorderJsonEl.textContent = JSON.stringify(preorderItems);
    }
  } else {
    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      try {
        preorderItems = JSON.parse(preorderJsonEl.textContent || '[]');
      } catch (e) {
        console.warn('[CCC] Failed to parse cart banner preorder items', e);
        preorderItems = [];
      }
    }

    // Legacy fallback: pipe-delimited product types only
    if (!preorderItems.length) {
      const preorderTypesStr = bannerEl.getAttribute('data-preorder-types') || '';
      preorderItems = preorderTypesStr
        ? preorderTypesStr.split('|').map((type) => ({ type, vendor: '', estimatedRestockDate: null }))
        : [];
    }

    // Do not early-return on hidden when we have no live island — still allow
    // initial paint when Liquid already showed the banner.
    if (bannerEl.hasAttribute('hidden')) return;
  }

  if (!(hasCoffee || hasPreorders)) return;

  const dateEl = bannerEl.querySelector('[data-cru-cart-ready-date]');
  const explanationEl = bannerEl.querySelector('[data-cru-cart-explanation]');

  if (!getUnifiedCartMessage) {
    if (dateEl) dateEl.textContent = 'calculated shortly';
    return;
  }

  let roastDateObj = null;
  if (hasCoffee) {
    // Prefer the synchronous cache so the banner date paints instantly on repeat
    // visits; only await the Google Calendar fetch when the cache is cold/stale.
    let info = cachedRoastDateSync();
    if (!info) info = await cachedRoastDate();
    if (info) {
      roastDateObj = new Date(info.roastDate);
    }
  }

  const unifiedMsg = getUnifiedCartMessage(
    hasCoffee,
    hasPreorders,
    preorderItems,
    roastDateObj
  );

  // The banner icon is a static clock SVG rendered by Liquid
  // (custom-roast-cart-banner.liquid) — deliberately one icon regardless of cart
  // mix, since the banner is about *timing*. We intentionally do NOT overwrite it:
  // setting textContent here replaced the SVG with an emoji and caused a visible
  // load flash (clean clock on first paint → box/cup emoji once JS ran).
  //
  // Only write text when it actually differs from what's already shown, so cart
  // morphs and repeat calls don't re-trigger an identical (but reflow-inducing) swap.
  if (dateEl && unifiedMsg.readyMessage && dateEl.textContent.trim() !== unifiedMsg.readyMessage.trim()) {
    dateEl.textContent = unifiedMsg.readyMessage;
  }
  if (explanationEl && unifiedMsg.explanation && explanationEl.textContent.trim() !== unifiedMsg.explanation.trim()) {
    explanationEl.textContent = unifiedMsg.explanation;
  }
}

// ─── Event Listeners ─────────────────────────────────────────────────────────

document.addEventListener(ThemeEvents.variantSelected, (e) => {
  if (e.target instanceof HTMLElement && e.target.closest('product-card, quick-add-dialog')) return;

  const id = e.detail?.resource?.variantId;
  if (!id) return;

  clearTimeout(_debounceTimer);
  applyProductPageStatus(Number(id));
});

document.addEventListener(ThemeEvents.variantUpdate, (e) => {
  const id = e.detail?.resource?.id;
  if (!id) return;
  // Debounce: Horizon fires one event per option group on page load.
  // Collapsing the burst prevents multiple concurrent async calls.
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => applyProductPageStatus(id), DEBOUNCE_MS);
});

// Horizon 4.2.0 re-renders the cart items section on a browser Back (bfcache /
// disk-cache restore) without dispatching cartLinesUpdate. The live state
// island inside it changes, so re-read it here or the banner stays stale.
// Called directly (no requestAnimationFrame): the event is dispatched after the morph has
// finished, and rAF callbacks are parked while the tab is hidden.
document.addEventListener(ThemeEvents.cartSectionRestored, () => {
  applyCartBanner();
});

// Attach the cart-update listener via a dynamic import so the hosted
// @shopify/events CDN bundle never blocks the initial banner paint below.
import('@shopify/events').then(({ StandardEvents }) => {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(() => {
        requestAnimationFrame(() => applyCartBanner());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[CCC] custom-roast-ui cart update failed:', error);
      });
  });
});

// ─── Initial Load ─────────────────────────────────────────────────────────────

// Product page — the Liquid block already renders the correct initial
// visibility for the selected variant (hidden/visible flags are set
// server-side). We only need to populate the roast date text node if the
// initially selected variant is 'Upcoming Roast', without toggling any
// visibility (to avoid a flash).
(async function initProductPageDateText() {
  const roastInfoPromise = stampUpcomingRoastControls();

  loadVariantMap();
  if (!_variantMap) {
    await roastInfoPromise;
    return;
  }

  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  const hiddenInput = document.querySelector('input[name="id"]');
  const initialId = urlVariant
    ? parseInt(urlVariant, 10)
    : hiddenInput
      ? parseInt(hiddenInput.value, 10)
      : null;

  if (!initialId) {
    await roastInfoPromise;
    return;
  }

  const variant = _variantMap.get(initialId);
  if (!variant) {
    await roastInfoPromise;
    return;
  }

  const isUpcoming = variant.title && variant.title.toLowerCase().includes('upcoming roast');
  const upcomingEl = document.querySelector('[data-cru-upcoming]');
  if (!isUpcoming || !upcomingEl) {
    await roastInfoPromise;
    return;
  }

  _pendingVariantId = initialId;
  const info = await roastInfoPromise;
  if (_pendingVariantId !== initialId) return;
  writeRoastCopy(upcomingEl, info);
})();

// Cart page — Liquid renders the banner visibility (hidden/shown) based on
// cart contents at render time. But dates are fetched from the Google Calendar
// API, so we need to populate them via JS on initial load.
(function initCartBannerDates() {
  applyCartBanner();
})();
